import { useState, useEffect } from "react";
import { useLocation, useParams } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  Users, Plus, Search, ArrowUpDown, Download, User, Music, 
  Mail, Phone, Instagram, Edit, Trash2, Calendar, CheckCircle, XCircle,
  MoreVertical, BarChart
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import ParticipantFormModal from "@/components/participants/participant-form-modal";
import type { Participant, Organisation } from "@shared/schema";

interface ParticipantWithStats extends Participant {
  performerCount?: number;
  actCount?: number;
}

export default function ParticipantsPage() {
  const { orgId } = useParams();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [filterType, setFilterType] = useState<"all" | "acts" | "events">("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState<Participant | null>(null);

  // If orgId is provided in URL, use it
  useEffect(() => {
    if (orgId) {
      setSelectedOrgId(parseInt(orgId));
    }
  }, [orgId]);

  // Determine which organizations the user has access to
  const availableOrgs = user?.role === 'god' 
    ? [] // God users need to fetch all orgs
    : user?.organisationUsers?.map(ou => ou.organisation) || [];

  // Fetch all organizations for god users
  const { data: allOrgs } = useQuery<Organisation[]>({
    queryKey: ['/api/organisations'],
    queryFn: async () => {
      const response = await fetch('/api/organisations');
      if (!response.ok) throw new Error("Failed to fetch organisations");
      return response.json();
    },
    enabled: user?.role === 'god',
  });

  const orgsToShow = user?.role === 'god' ? allOrgs || [] : availableOrgs;

  // Check if user needs to select an organization
  useEffect(() => {
    if (!orgId && orgsToShow.length > 1) {
      setShowOrgSelector(true);
    } else if (!orgId && orgsToShow.length === 1) {
      // Auto-select if only one org
      setSelectedOrgId(orgsToShow[0].id);
      setLocation(`/participants/${orgsToShow[0].id}`);
    } else if (!orgId && orgsToShow.length === 0) {
      // No organizations available
      toast({
        title: "No Access",
        description: "You don't have access to any organizations.",
        variant: "destructive",
      });
      setLocation("/");
    }
  }, [orgId, orgsToShow, setLocation]);

  // Fetch participants for the selected organization
  const { data: participants, isLoading } = useQuery<ParticipantWithStats[]>({
    queryKey: [`/api/participants?organisationId=${selectedOrgId}`],
    queryFn: async () => {
      const response = await fetch(`/api/participants?organisationId=${selectedOrgId}`);
      if (!response.ok) throw new Error("Failed to fetch participants");
      return response.json();
    },
    enabled: !!selectedOrgId,
  });

  // Delete participant mutation
  const deleteParticipantMutation = useMutation({
    mutationFn: async (participantId: number) => {
      const response = await fetch(`/api/participants/${participantId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error("Failed to delete participant");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/participants`] });
      toast({
        title: "Participant Deleted",
        description: "The participant has been deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Filter and sort participants
  const filteredParticipants = participants?.filter(p => {
    // Search filter
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.instagram?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Type filter
    let matchesType = true;
    if (filterType === "acts") {
      matchesType = (p.actCount || 0) > 0;
    } else if (filterType === "events") {
      matchesType = (p.performerCount || 0) > 0;
    }
    
    return matchesSearch && matchesType;
  }) || [];

  const sortedParticipants = [...filteredParticipants].sort((a, b) => {
    switch (sortBy) {
      case "name":
        return a.name.localeCompare(b.name);
      case "events":
        return (b.performerCount || 0) - (a.performerCount || 0);
      case "acts":
        return (b.actCount || 0) - (a.actCount || 0);
      default:
        return 0;
    }
  });

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Instagram', 'Events Attended', 'Registered as Act'];
    const rows = sortedParticipants.map(p => [
      p.name,
      p.email || '',
      p.phone || '',
      p.instagram || '',
      (p.performerCount || 0).toString(),
      (p.actCount || 0) > 0 ? 'Yes' : 'No'
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => 
        cell.includes(',') || cell.includes('"') 
          ? `"${cell.replace(/"/g, '""')}"` 
          : cell
      ).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `participants_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <PageBackground>
      <AppHeader backUrl="/" />
      
      {/* Organization Selector Dialog */}
      <Dialog open={showOrgSelector} onOpenChange={setShowOrgSelector}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Organisation</DialogTitle>
            <DialogDescription>
              Choose which organisation's participants you want to view.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            {orgsToShow.map(org => (
              <Button
                key={org.id}
                variant="outline"
                className="justify-start"
                onClick={() => {
                  setSelectedOrgId(org.id);
                  setShowOrgSelector(false);
                  setLocation(`/participants/${org.id}`);
                }}
              >
                {org.name}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 p-6"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
            }}>
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-slate-900 drop-shadow-sm flex items-center gap-3">
                  <Users className="h-8 w-8 text-green-600" />
                  Participants
                </h1>
                
                {/* Organization Switcher */}
                {orgsToShow.length > 1 && selectedOrgId && (
                  <div className="mt-3">
                    <Select 
                      value={selectedOrgId.toString()} 
                      onValueChange={(value) => {
                        const newOrgId = parseInt(value);
                        setSelectedOrgId(newOrgId);
                        setLocation(`/participants/${newOrgId}`);
                      }}
                    >
                      <SelectTrigger className="w-full sm:w-[300px] bg-white/30 border-white/30">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {orgsToShow.map(org => (
                          <SelectItem key={org.id} value={org.id.toString()}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Single org display */}
                {orgsToShow.length === 1 && (
                  <p className="text-slate-600 mt-1">
                    {selectedOrgId && orgsToShow.find(o => o.id === selectedOrgId)?.name}
                  </p>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleExportCSV}
                  variant="outline"
                  disabled={!sortedParticipants.length}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
                <Button 
                  onClick={() => setShowCreateModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Participant
                </Button>
              </div>
            </div>

            {/* Search and Filter Controls */}
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, or Instagram..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full sm:w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Sort by Name</SelectItem>
                    <SelectItem value="events">Sort by Events Attended</SelectItem>
                    <SelectItem value="acts">Sort by Act Registration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={filterType === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterType("all")}
                  className={filterType === "all" ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                >
                  All Participants
                  {participants && (
                    <Badge variant="secondary" className="ml-2">
                      {participants.length}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant={filterType === "acts" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterType("acts")}
                  className={filterType === "acts" ? "bg-green-600 hover:bg-green-700 text-white" : ""}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Registered as Act
                  {participants && (
                    <Badge variant="secondary" className="ml-2">
                      {participants.filter(p => (p.actCount || 0) > 0).length}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant={filterType === "events" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterType("events")}
                  className={filterType === "events" ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Attended Event
                  {participants && (
                    <Badge variant="secondary" className="ml-2">
                      {participants.filter(p => (p.performerCount || 0) > 0).length}
                    </Badge>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Participants List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Users className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700" />
              <p className="text-slate-700">Loading participants...</p>
            </div>
          </div>
        ) : sortedParticipants.length === 0 ? (
          <Card className="bg-white/20 backdrop-blur-xl border-white/30">
            <CardContent className="text-center py-12">
              <User className="h-12 w-12 mx-auto mb-4 text-slate-400" />
              <p className="text-slate-600">
                {searchQuery ? "No participants found matching your search." : "No participants yet."}
              </p>
              {!searchQuery && (
                <Button 
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Participant
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Mobile Cards View */}
            <div className="md:hidden space-y-3">
              {sortedParticipants.map((participant) => (
                <Card 
                  key={participant.id}
                  className="bg-white/20 backdrop-blur-xl border-white/30"
                >
                  <CardContent className="p-4">
                    {/* Header with name and actions */}
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900 text-lg">{participant.name}</h3>
                        {participant.lookingForBand && (
                          <Badge variant="secondary" className="mt-1">
                            <Music className="h-3 w-3 mr-1" />
                            Looking for Band
                          </Badge>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditingParticipant(participant)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {(participant.performerCount || 0) > 0 && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => setLocation(`/analytics/${selectedOrgId}/performer/${participant.id}?organisationId=${selectedOrgId}&tab=performers&eventType=&eventId=&search=&from=participants`)}
                              >
                                <BarChart className="h-4 w-4 mr-2" />
                                Event Management Analytics
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => {
                              if (confirm(`Are you sure you want to delete ${participant.name}?`)) {
                                deleteParticipantMutation.mutate(participant.id);
                              }
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {/* Contact info */}
                    <div className="space-y-1 text-sm mb-3">
                      {participant.email && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="h-3 w-3" />
                          <a href={`mailto:${participant.email}`} className="hover:underline truncate">
                            {participant.email}
                          </a>
                        </div>
                      )}
                      {participant.phone && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="h-3 w-3" />
                          <a href={`tel:${participant.phone}`} className="hover:underline">
                            {participant.phone}
                          </a>
                        </div>
                      )}
                      {participant.instagram && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Instagram className="h-3 w-3" />
                          <a 
                            href={`https://instagram.com/${participant.instagram.replace('@', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            {participant.instagram}
                          </a>
                        </div>
                      )}
                    </div>
                    
                    {/* Stats */}
                    <div className="flex justify-between items-center pt-3 border-t border-white/20">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-blue-600" />
                        <span className="text-sm text-slate-700">Events:</span>
                        <Badge variant="outline" className="bg-blue-50">
                          {participant.performerCount || 0}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        {(participant.actCount || 0) > 0 ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-700">Registered Act</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-slate-400" />
                            <span className="text-sm text-slate-500">Not an Act</span>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Desktop Table View */}
            <Card className="hidden md:block bg-white/20 backdrop-blur-xl border-white/30 overflow-hidden">
              <Table>
              <TableHeader>
                <TableRow className="bg-white/20 backdrop-blur-sm border-b border-white/20">
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="text-center">Events Attended</TableHead>
                  <TableHead className="text-center">Registered as Act</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedParticipants.map((participant) => (
                  <TableRow 
                    key={participant.id}
                    className="hover:bg-white/20 backdrop-blur-sm transition-all duration-200 border-b border-white/10"
                  >
                    <TableCell>
                      <div className="font-medium text-slate-900">{participant.name}</div>
                      {participant.lookingForBand && (
                        <Badge variant="secondary" className="mt-1">
                          <Music className="h-3 w-3 mr-1" />
                          Looking for Band
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        {participant.email && (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Mail className="h-3 w-3" />
                            <a href={`mailto:${participant.email}`} className="hover:underline">
                              {participant.email}
                            </a>
                          </div>
                        )}
                        {participant.phone && (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Phone className="h-3 w-3" />
                            <a href={`tel:${participant.phone}`} className="hover:underline">
                              {participant.phone}
                            </a>
                          </div>
                        )}
                        {participant.instagram && (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Instagram className="h-3 w-3" />
                            <a 
                              href={`https://instagram.com/${participant.instagram.replace('@', '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {participant.instagram}
                            </a>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-blue-50">
                        {participant.performerCount || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {(participant.actCount || 0) > 0 ? (
                        <Badge className="bg-green-100 text-green-800 border-green-200">
                          Yes
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">
                          No
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditingParticipant(participant)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {(participant.performerCount || 0) > 0 && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => setLocation(`/analytics/${selectedOrgId}/performer/${participant.id}?organisationId=${selectedOrgId}&tab=performers&eventType=&eventId=&search=&from=participants`)}
                              >
                                <BarChart className="h-4 w-4 mr-2" />
                                Event Management Analytics
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => {
                              if (confirm(`Are you sure you want to delete ${participant.name}?`)) {
                                deleteParticipantMutation.mutate(participant.id);
                              }
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          </>
        )}
      </main>

      {/* Create/Edit Participant Modal */}
      <ParticipantFormModal
        isOpen={showCreateModal || !!editingParticipant}
        onClose={() => {
          setShowCreateModal(false);
          setEditingParticipant(null);
        }}
        participant={editingParticipant || undefined}
        organisationId={selectedOrgId || undefined}
      />
    </PageBackground>
  );
}